import { FC } from "react";

// @mui material components
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";

// Material Dashboard 2 React components
import MDBox from "../../atoms/MDBox";
import MDTypography from "../../atoms/MDTypography";

// Custom styles for the MDSnackbar
import MDSnackbarIconRoot from "./MDSnackbarIconRoot";

// Types
import { MDSnackbarProps } from "./types";

const MDSnackbar: FC<MDSnackbarProps> = ({ 
  color = "info", 
  icon, 
  title, 
  dateTime, 
  content, 
  close, 
  bgWhite = false,
  open = true,
  ...rest 
}) => {
  // For now, we'll use basic dark mode detection or you can pass it as a prop
  const darkMode = false; // You can implement proper theme context later

  let titleColor: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark" | "light" | "text" | "inherit" | "white";
  let dateTimeColor: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark" | "light" | "text" | "inherit" | "white";
  let dividerColor: boolean;

  if (bgWhite) {
    titleColor = color;
    dateTimeColor = "dark";
    dividerColor = false;
  } else if (color === "light") {
    titleColor = darkMode ? "inherit" : "dark";
    dateTimeColor = darkMode ? "inherit" : "text";
    dividerColor = false;
  } else {
    titleColor = "white";
    dateTimeColor = "white";
    dividerColor = true;
  }

  return (
    <Snackbar
      open={open}
      TransitionComponent={Fade}
      autoHideDuration={5000}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      {...rest}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={close}>
          <Icon fontSize="small">close</Icon>
        </IconButton>
      }
    >
      <MDBox
        variant={bgWhite ? "contained" : "gradient"}
        bgColor={bgWhite ? "white" : color}
        minWidth="21.875rem"
        maxWidth="100%"
        shadow="md"
        borderRadius="md"
        p={1}
        sx={{
          backgroundColor: ({ palette }: any) =>
            darkMode ? palette.background?.card : palette[color] || palette.common.white,
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          color="dark"
          p={1.5}
        >
          <MDBox display="flex" alignItems="center" lineHeight={0}>
            <MDSnackbarIconRoot fontSize="small" ownerState={{ color, bgWhite }}>
              {icon}
            </MDSnackbarIconRoot>
            <MDTypography
              variant="button"
              fontWeight="medium"
              color={titleColor}
              textGradient={bgWhite}
            >
              {title}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" lineHeight={0}>
            <MDTypography variant="caption" color={dateTimeColor}>
              {dateTime}
            </MDTypography>
            <Icon
              sx={{
                color: ({ palette: { dark, common } }: any) =>
                  (bgWhite && !darkMode) || color === "light" ? (dark?.main || '#344767') : common.white,
                fontWeight: ({ typography: { fontWeightBold } }: any) => fontWeightBold,
                cursor: "pointer",
                marginLeft: 2,
                transform: "translateY(-1px)",
              }}
              onClick={close}
            >
              close
            </Icon>
          </MDBox>
        </MDBox>
        <Divider sx={{ margin: 0 }} light={dividerColor} />
        <MDBox
          p={1.5}
          sx={{
            fontSize: ({ typography }: any) => typography.pxToRem ? typography.pxToRem(14) : '0.875rem',
            color: ({ palette: { common, text } }: any) => {
              let colorValue = bgWhite || color === "light" ? text.main : common.white;

              if (darkMode) {
                colorValue = color === "light" ? "inherit" : common.white;
              }

              return colorValue;
            },
          }}
        >
          {content}
        </MDBox>
      </MDBox>
    </Snackbar>
  );
};

export default MDSnackbar;